import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PropertyPublic, StoreThemeId } from "@townsy/contracts";
import { fetchProperty } from "../api/endpoints";
import { queryKeys, useBootstrapQuery } from "../api/queries";
import { useTownSession } from "../app/TownSessionContext";
import { useTownStore } from "../state/store";
import { Dialog } from "./Dialog";
import { StoreThemeDialog, StoreThemeThumbnail } from "./StoreThemeDialog";
import { STORE_THEME_OPTIONS, storeThemeLabel, themePickerAccess, type ThemePickerAccess } from "./storeThemeLogic";

/**
 * The one store interior picker of the page (town or 2D pages), opened from the "purchase
 * complete" toast, the receipt, the checkout's last step and the store panel. It re-reads the
 * store with the visitor's own session (the API lists `edit_interior` for the current owner only)
 * and opens the picker only then; otherwise it says why. The server re-checks on save.
 */
export function StoreThemeHost() {
  const request = useTownStore((s) => s.themePicker);
  if (!request) return null;
  return <StoreThemeHostDialog key={request.id} propertyId={request.propertyId} afterPurchase={request.source === "purchase"} />;
}

/** The visitor's own read of a store, refreshed when a picker opens (a cached copy may predate a purchase). */
export function useOwnStoreRead(propertyId: string | null) {
  return useQuery({
    queryKey: queryKeys.property(propertyId ?? "none"),
    queryFn: () => fetchProperty(propertyId as string),
    enabled: Boolean(propertyId),
    staleTime: 0,
    refetchOnMount: "always",
  });
}

function StoreThemeHostDialog({ propertyId, afterPurchase }: { propertyId: string; afterPurchase: boolean }) {
  const close = useTownStore((s) => s.closeThemePicker);
  const pushToast = useTownStore((s) => s.pushToast);
  const session = useTownSession();
  const qc = useQueryClient();
  const boot = useBootstrapQuery();
  const signedIn = Boolean(boot.data?.session.userId);
  const own = useOwnStoreRead(signedIn ? propertyId : null);
  const property: PropertyPublic | null = own.data ?? null;
  const access = boot.isPending ? { state: "checking" as const, message: "Checking your session…" } : themePickerAccess({ property, signedIn, fetching: own.isFetching, error: own.error });

  if (access.state !== "ready" || !property) {
    return <ThemePickerUnavailable access={access} onClose={close} onRetry={access.state === "error" || access.state === "not_owner" ? () => void own.refetch() : undefined} />;
  }
  const current: StoreThemeId = property.interiorTheme ?? "general";
  return (
    <StoreThemeDialog
      propertyId={propertyId}
      businessName={property.placement?.businessName ?? property.address}
      current={current}
      lead={afterPurchase ? "Congratulations on your store! Pick the interior your visitors walk into." : null}
      onClose={close}
      onSaved={(res) => {
        close();
        pushToast(`Interior changed to ${storeThemeLabel(res.interiorTheme) ?? res.interiorTheme}. Everyone inside sees it now.`, "success");
        if (session) session.applySavedTheme(propertyId, res);
        else {
          const cached = qc.getQueryData<PropertyPublic>(queryKeys.property(propertyId));
          if (cached) qc.setQueryData(queryKeys.property(propertyId), { ...cached, interiorTheme: res.interiorTheme });
          void qc.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
          void qc.invalidateQueries({ queryKey: queryKeys.properties });
        }
      }}
    />
  );
}

/** Why the picker cannot open right now (checking, sign in, not the owner, load error). */
export function ThemePickerUnavailable({ access, onClose, onRetry }: { access: ThemePickerAccess; onClose(): void; onRetry?: () => void }) {
  return (
    <Dialog title="Store interior" onClose={onClose} describedBy="theme-picker-unavailable">
      <div className="stack" data-testid="store-theme-unavailable" data-state={access.state}>
        <p id="theme-picker-unavailable" className={access.state === "checking" ? "muted" : "notice notice-info"} role="status" style={{ margin: 0 }}>
          {access.message}
        </p>
        <div className="row">
          {onRetry ? (
            <button type="button" className="btn btn-primary" onClick={onRetry}>
              Check again
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * The receipt's "Choose your store's interior" call to action for a completed store purchase. The
 * button opens the picker; when the visitor no longer owns the store it is disabled with the reason.
 */
export function ReceiptInteriorCallout({
  access,
  currentTheme,
  onChoose,
}: {
  access: ThemePickerAccess;
  currentTheme: StoreThemeId | null;
  onChoose(): void;
}) {
  const blocked = access.state === "not_owner" || access.state === "not_store" || access.state === "sign_in";
  const theme: StoreThemeId = currentTheme ?? "general";
  return (
    <section className="receipt-interior" aria-labelledby="receipt-interior-title" data-testid="receipt-choose-interior">
      <div className="receipt-interior-thumb">
        <StoreThemeThumbnail themeId={theme} />
      </div>
      <div className="stack" style={{ gap: 6, minWidth: 0 }}>
        <h2 id="receipt-interior-title" style={{ margin: 0 }}>
          Make the store yours
        </h2>
        <p className="small" style={{ margin: 0 }}>
          Your store's interior is <strong>{storeThemeLabel(theme) ?? theme}</strong>. Pick any of the {STORE_THEME_OPTIONS.length} presets (any one fits any
          business); it's free and you can change it again any time.
        </p>
        <div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={blocked}
            aria-describedby={blocked ? "receipt-interior-reason" : undefined}
            onClick={onChoose}
            data-testid="receipt-choose-interior-button"
          >
            Choose your store's interior
          </button>
        </div>
        {blocked ? (
          <span id="receipt-interior-reason" className="hint" data-testid="receipt-choose-interior-reason">
            {access.message}
          </span>
        ) : null}
      </div>
    </section>
  );
}
